import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { TipoOcorrenciaCompraEnum } from '@shared/enums/compras/tipo-ocorrencia-compra.enum';
import { CompraOcorrenciaStruct } from '@shared/services/structs/compras/compra-ocorrencia.struct';
import { VinculoPedidoNotaStruct } from '@shared/services/structs/orquestrador-compras/vinculo-pedido-nota.struct';
import type { KanbanCard, KanbanStage, Person } from '../../../processo-compras.models';
import { CardDetalhe, formatCodigoCompra } from '../card-edit-dialog.types';

/** Linha do bloco "Nota fiscal": NF liberada para a gaveta no cancelamento ou que continua vinculada ao pedido. */
interface NotaCancelamento {
  idNFeEntrada: number;
  isLiberada: boolean;
  nota?: VinculoPedidoNotaStruct;
}

/**
 * Etapa Cancelado: motivo do cancelamento, etapa de origem, autor, data, referência e o que aconteceu com a NF.
 * Somente leitura — sem outputs; o shell bloqueia o salvamento em estados finais.
 * O cancelamento é a ocorrência do tipo Cancelamento no histórico (arraste, lote ou botão), que o shell carrega
 * depois de abrir; a etapa de origem cai na etapa anterior gravada nela quando a compra não a tiver.
 */
@Component({
  selector: 'app-ced-stage-cancelado',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, AlertCardComponent],
  templateUrl: './ced-stage-cancelado.component.html',
  styleUrl: './ced-stage-cancelado.component.css',
})
export class CedStageCanceladoComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) card!: KanbanCard;
  @Input() detalhe?: CardDetalhe;
  @Input() ocorrencias: CompraOcorrenciaStruct[] = [];
  @Input() usersById?: Map<number, Person>;
  @Input() allStages: KanbanStage[] = [];

  canceladoMotivo = '';
  canceladoEtapaOrigem = '';
  canceladoPor = '';
  canceladoData = '';
  canceladoReferencia = '';
  notas: NotaCancelamento[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(private notasService: NotaFiscalEntradaService) {}

  ngOnChanges(): void {
    this.montarCancelamento();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** D-05: abre o cadastro da nota em nova aba. */
  abrirNota(idNFeEntrada: number): void {
    window.open(`${window.location.origin}/suprimentos/nota-fiscal-entrada/documento/${idNFeEntrada}`, '_blank', 'noopener');
  }

  private montarCancelamento(): void {
    const d = this.detalhe;
    const c = d?.compra;
    const cancelamento = this.ocorrencias
      .filter(o => o.idTipoOcorrenciaCompra === TipoOcorrenciaCompraEnum.Cancelamento)
      .sort((a, b) => new Date(b.dataOcorrencia).getTime() - new Date(a.dataOcorrencia).getTime())[0];
    const etapaOrigem = c?.idEtapaKanbanCancelamento
      ? this.allStages.find(s => s.idEtapaKanban === c.idEtapaKanbanCancelamento)
      : undefined;
    const dataCancelamento = cancelamento?.dataOcorrencia || c?.datetimeAlteracao || c?.datetimeInclusao;

    this.canceladoMotivo = c?.motivoCancelamento || cancelamento?.justificativa || '';
    this.canceladoEtapaOrigem = etapaOrigem?.name || cancelamento?.nomeEtapaKanbanAnterior || 'Etapa anterior não identificada';
    this.canceladoPor = cancelamento?.nomeUsuario
      || this.usersById?.get(cancelamento?.idUsuario ?? -1)?.name
      || this.card.respName || this.card.resp || 'Usuário não identificado';
    this.canceladoData = dataCancelamento
      ? new Date(dataCancelamento).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
      : '';
    this.canceladoReferencia = this.card.id || formatCodigoCompra(c?.codigo);

    // D-05: a situação atual prevalece — nota ainda vinculada aparece como "Continua vinculada".
    const idsVinculadas = [...new Set((d?.vinculosNota ?? []).map(v => v.idNFeEntrada))];
    const idsLiberadas = this.extrairIdsNotasDesvinculadas(cancelamento?.dados).filter(id => !idsVinculadas.includes(id));
    this.notas = [
      ...idsLiberadas.map(idNFeEntrada => ({ idNFeEntrada, isLiberada: true })),
      ...idsVinculadas.map(idNFeEntrada => ({ idNFeEntrada, isLiberada: false })),
    ];
    if (this.notas.length) void this.carregarNotas();
  }

  /** Número/série e emitente das notas, uma chamada; se falhar, as linhas ficam com "NF-e #id". */
  private async carregarNotas(): Promise<void> {
    try {
      const resposta = await firstValueFrom(this.notasService.contextosVinculoPedido(this.notas.map(n => n.idNFeEntrada)).pipe(takeUntil(this.destroy$)));
      if (resposta.isError) return;
      const notasPorId = new Map(resposta.lista.filter(c => !c.isError && c.nota).map(c => [c.nota.idNFeEntrada, c.nota]));
      this.notas = this.notas.map(n => ({ ...n, nota: notasPorId.get(n.idNFeEntrada) }));
    } catch {
      // Sem os dados da nota a linha mantém o identificador; o bloco continua útil.
    }
  }

  /** `dados` da ocorrência Cancelamento: `idsNotasDesvinculadas` só existe quando a etapa Cancelado liberou NFs. */
  private extrairIdsNotasDesvinculadas(dados?: string | null): number[] {
    if (!dados) return [];
    try {
      const ids: unknown = JSON.parse(dados)?.idsNotasDesvinculadas;
      return Array.isArray(ids) ? [...new Set(ids.filter((id): id is number => Number.isInteger(id) && id > 0))] : [];
    } catch {
      return [];
    }
  }

}
